/**
 * Tablero de uso (2026-10-10, plan "qué nos falta", bloque 1). Solo administradores.
 *
 * Para el piloto: quién usa el asistente, cuánto, en qué modo, cuánto resuelve el motor y cuánto
 * cae al agente anterior, qué derivó y qué votaron. Lee GET /api/uso con el token personal.
 */
import { useEffect, useState } from 'react'
import { API_BASE } from '../api/client'
import { Historial } from './Historial'

interface Fila { [k: string]: string | number | null }
interface UsoDatos {
  dias: number
  desde: string
  consultas: number
  personas: number
  casos: number
  con_producto: number
  derivaciones: number
  segundos_p50: number | null
  por_persona: { email: string; nombre: string; consultas: number; ultima: string }[]
  por_sucursal: { sucursal: string; consultas: number }[]
  por_dia: { dia: string; consultas: number }[]
  por_modo: { modo: string; consultas: number }[]
  por_via: { via: string; consultas: number }[]
  votos: { up?: number; down?: number; vendido?: number; otro_producto?: number; no_vendido?: number }
  votos_negativos: { ts: string; categoria: string | null; comentario: string | null; mensaje: string }[]
  al_agente_anterior: { ts: string; mensaje: string }[]
}

const PERIODOS = [7, 14, 30]

function fecha(ts: string) {
  return new Date(ts).toLocaleString('es-BO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function Barras({ filas, clave, etiqueta }: { filas: Fila[]; clave: string; etiqueta: string }) {
  const max = Math.max(1, ...filas.map((f) => Number(f[clave]) || 0))
  return (
    <div className="flex flex-col gap-1">
      {filas.map((f) => (
        <div key={String(f[etiqueta])} className="grid items-center gap-2" style={{ gridTemplateColumns: 'minmax(0, 9rem) 1fr 2.5rem' }}>
          <span className="truncate text-[13px]" style={{ color: 'var(--color-text-muted)' }}>{String(f[etiqueta])}</span>
          <span className="h-3 rounded" style={{ width: `${(Number(f[clave]) / max) * 100}%`, background: 'var(--color-brand)', minWidth: 2 }} />
          <span className="text-right text-[13px] tabular-nums">{String(f[clave])}</span>
        </div>
      ))}
      {filas.length === 0 && <span className="text-[13px]" style={{ color: 'var(--color-text-soft)' }}>Sin datos</span>}
    </div>
  )
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl p-4" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
      <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>{titulo}</h2>
      {children}
    </section>
  )
}

const MOTIVO_NO: Record<string, string> = {
  caro: 'caro', sin_stock: 'no había', solo_consulta: 'solo consultaba', no_servia: 'la sugerencia no servía',
  partial: 'parcial', wrong_dose: 'dosis equivocada', wrong_pa: 'molécula equivocada', other: 'otro', correct: 'correcto',
}

export function Uso({ onVolver }: { onVolver: () => void }) {
  const [dias, setDias] = useState(14)
  const [datos, setDatos] = useState<UsoDatos | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    setError(null)
    fetch(`${API_BASE}/uso?dias=${dias}`, { headers: { 'X-Device-Token': localStorage.getItem('device_token') ?? '' } })
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 403 ? 'Solo para administradores.' : `Error ${r.status}`)
        return r.json()
      })
      .then((d) => vivo && setDatos(d))
      .catch((e) => vivo && setError(String(e.message || e)))
    return () => {
      vivo = false
    }
  }, [dias])

  const pct = (n: number) => (datos && datos.consultas ? `${Math.round((n / datos.consultas) * 100)}%` : '–')
  const v1 = datos?.por_via.find((v) => v.via.startsWith('agente anterior'))?.consultas ?? 0
  // 20-oct: "¿Lo vendiste?" en las respuestas con producto
  const vend = datos?.votos.vendido ?? 0
  const otro = datos?.votos.otro_producto ?? 0
  const noVend = datos?.votos.no_vendido ?? 0
  const conVenta = vend + otro + noVend
  const cifras = datos
    ? [
        ['Consultas', String(datos.consultas)],
        ['Personas', String(datos.personas)],
        ['Casos', String(datos.casos)],
        ['Con producto', pct(datos.con_producto)],
        ['Derivaciones', String(datos.derivaciones)],
        ['Cayó al agente anterior', pct(v1)],
        ['Vendió lo sugerido', conVenta ? `${Math.round((vend / conVenta) * 100)}% (${vend} de ${conVenta})` : 'sin respuestas'],
        ['Vendió otro / no se vendió', `${otro} / ${noVend}`],
        ['Le sirvió (sí / no)', `${datos.votos.up ?? 0} / ${datos.votos.down ?? 0}`],
        ['Respuesta (mediana)', datos.segundos_p50 != null ? `${datos.segundos_p50} s` : '–'],
      ]
    : []

  return (
    <div className="min-h-full px-4 py-5" style={{ background: 'var(--color-bg)' }}>
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-[20px] font-semibold" style={{ textWrap: 'balance' }}>Uso del asistente</h1>
            <p className="text-[13px]" style={{ color: 'var(--color-text-muted)' }}>
              Consultas del personal con usuario{datos ? ` desde el ${datos.desde}` : ''}. Las pruebas automáticas no cuentan.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {PERIODOS.map((p) => (
              <button key={p} type="button" onClick={() => setDias(p)} className="min-h-[36px] rounded-full px-3 text-[13px]"
                style={{ border: '1px solid var(--color-border)', background: p === dias ? 'var(--color-brand-soft)' : 'var(--color-surface)',
                         color: p === dias ? 'var(--color-brand-ink)' : 'var(--color-text)' }}>
                {p} días
              </button>
            ))}
            <button type="button" onClick={onVolver} className="min-h-[36px] rounded-full px-3 text-[13px] underline"
              style={{ color: 'var(--color-text-muted)' }}>
              volver
            </button>
          </div>
        </div>

        {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        {!datos && !error && <p style={{ color: 'var(--color-text-soft)' }}>Cargando…</p>}

        {datos && (
          <>
            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(9.5rem, 1fr))' }}>
              {cifras.map(([k, v]) => (
                <div key={k} className="rounded-xl p-3" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                  <div className="text-[12px]" style={{ color: 'var(--color-text-muted)' }}>{k}</div>
                  <div className="text-[22px] font-semibold tabular-nums">{v}</div>
                </div>
              ))}
            </div>

            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))' }}>
              <Seccion titulo="Por persona">
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead><tr style={{ color: 'var(--color-text-muted)' }}><th className="text-left font-normal">Persona</th><th className="text-right font-normal">Consultas</th><th className="text-right font-normal">Última</th></tr></thead>
                    <tbody>
                      {datos.por_persona.map((p) => (
                        <tr key={p.email}><td className="py-1">{p.nombre || p.email}</td><td className="text-right tabular-nums">{p.consultas}</td><td className="text-right tabular-nums" style={{ color: 'var(--color-text-muted)' }}>{fecha(p.ultima)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  {datos.por_persona.length === 0 && <span className="text-[13px]" style={{ color: 'var(--color-text-soft)' }}>Nadie lo usó en este período.</span>}
                </div>
              </Seccion>
              <Seccion titulo="Por día"><Barras filas={datos.por_dia} clave="consultas" etiqueta="dia" /></Seccion>
              <Seccion titulo="Por sucursal"><Barras filas={datos.por_sucursal} clave="consultas" etiqueta="sucursal" /></Seccion>
              <Seccion titulo="Quién respondió"><Barras filas={datos.por_via} clave="consultas" etiqueta="via" /></Seccion>
              <Seccion titulo="No se vendió o no sirvió">
                <ul className="flex flex-col gap-2 text-[13px]">
                  {datos.votos_negativos.map((v, i) => (
                    <li key={i}><span style={{ color: 'var(--color-text-muted)' }}>{fecha(v.ts)} · {MOTIVO_NO[v.categoria ?? ''] ?? v.categoria ?? 'sin motivo'}</span><br />«{v.mensaje}» {v.comentario ? `— ${v.comentario}` : ''}</li>
                  ))}
                  {datos.votos_negativos.length === 0 && <li style={{ color: 'var(--color-text-soft)' }}>Ninguno.</li>}
                </ul>
              </Seccion>
            </div>

            <Historial dias={dias} sucursales={datos.por_sucursal.map((s) => s.sucursal)}
              personas={datos.por_persona.map((p) => ({ email: p.email, nombre: p.nombre }))} />

            <Seccion titulo="Lo que cayó al agente anterior (para sumar al motor)">
              <ul className="flex flex-col gap-1 text-[13px]">
                {datos.al_agente_anterior.map((m, i) => (
                  <li key={i}><span className="tabular-nums" style={{ color: 'var(--color-text-muted)' }}>{fecha(m.ts)}</span> · {m.mensaje}</li>
                ))}
                {datos.al_agente_anterior.length === 0 && <li style={{ color: 'var(--color-text-soft)' }}>Nada: todo lo resolvió el motor.</li>}
              </ul>
            </Seccion>
          </>
        )}
      </div>
    </div>
  )
}
